import {Component, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {OperatorFunction, Subscription} from 'rxjs';
import {fetchCurrentUserAction, SecuritySelectors} from '@store/security';
import {UserAuthenticatedInterface} from '@shared/interfaces/user.interface';
import {GeneralActions, GeneralSelectors} from '@store/general';
import {filter, take} from 'rxjs/operators';
import {FaviconService} from '@core/services/favicon/favicon.service';
import {PublisherInterface} from '@shared/interfaces/publisher.interface';
import {MessageService} from '@/component/messages/message.service';
import {RoutingService} from '@component/routing/routing.service';
import {environment} from '@environment/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    readonly #subscriptions: Subscription[] = [];

    readonly #store: Store;

    readonly #faviconService: FaviconService;

    readonly #messageService: MessageService;

    readonly #routingService: RoutingService;

    currentUser?: UserAuthenticatedInterface;

    publisher?: PublisherInterface;

    public constructor(
        store: Store,
        faviconService: FaviconService,
        messageService: MessageService,
        routingService: RoutingService,
    ) {
        this.#store = store;
        this.#faviconService = faviconService;
        this.#messageService = messageService;
        this.#routingService = routingService;
    }

    ngOnInit(): void {
        this.#store.select(GeneralSelectors.selectPublisher)
            .pipe(filter(publisher => undefined !== publisher) as OperatorFunction<PublisherInterface | undefined, PublisherInterface>, take(1))
            .subscribe(publisher => this.#handleSelectPublisher(publisher));

        this.#subscriptions.push(
            this.#store.select(SecuritySelectors.selectCurrentUser).subscribe(currentUser => this.#handleSelectCurrentUser(currentUser)),
        );

        this.#store.dispatch(GeneralActions.fetchPublisher());

        this.#setGoogleTagManager();
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    #handleSelectPublisher(publisher: PublisherInterface): void {
        this.publisher = publisher;
        this.#faviconService.load(publisher);
        this.#store.dispatch(fetchCurrentUserAction());
    }

    #handleSelectCurrentUser(currentUser?: UserAuthenticatedInterface): void {
        if (undefined === currentUser) {
            return;
        }

        this.#messageService.load(this.currentUser = currentUser);
    }

    #setGoogleTagManager(): void {
        const hostname = window.location.hostname.toLowerCase();
        const hoHosts = ['boomacademie', 'boom-ho', 'boom-academie'];
        const voHosts = ['boomdigitaal', 'boom-vo', 'boomonderwijs'];

        const isHO = hoHosts.some(part => hostname.includes(part));
        const isVO = voHosts.some(part => hostname.includes(part));

        let gtmId: string | null = null;

        if (isHO) {
            gtmId = environment.gtm.idHo;
        } else if (isVO) {
            gtmId = environment.gtm.idVo;
        }
        if (gtmId) {
            this.#addGtmToHead(gtmId);
            this.#addGtmToBody(gtmId);
        } else {
            console.warn('Geen GTM-ID gevonden voor host:', hostname);
        }
    }

    #addGtmToHead(gtmId: string): void {
        const script = document.createElement('script');

        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `;
        document.head.appendChild(script);
    }

    #addGtmToBody(gtmId: string): void {
        const noscript = document.createElement('noscript');

        noscript.innerHTML = `
          <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
          height="0" width="0" style="display:none;visibility:hidden"></iframe>
        `;
        document.body.insertBefore(noscript, document.body.firstChild);
    }
}
